import React from 'react';
import { motion } from 'framer-motion';
import {
  FaMapMarkerAlt,
  FaClock,
  FaCheck,
  FaFeatherAlt,
  FaRegLightbulb,
  FaRocket,
  FaRegGem
} from 'react-icons/fa';
import Section from './layout/Section';
import ScrollReveal from './animations/ScrollReveal';
import SpotlightCard from './effects/SpotlightCard';
import TerminalFrame from './effects/TerminalFrame';
import { profile, about } from '../data/profile';

const philosophyIcons = {
  simple: FaFeatherAlt,
  understand: FaRegLightbulb,
  ship: FaRocket,
  details: FaRegGem
};

export default function About() {
  return (
    <Section
      id="about"
      align="left"
      eyebrow="About"
      title="I build software the way I’d want it built for me."
      className="bg-slate-50 dark:bg-slate-900"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-dotted opacity-40" />

      <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16">
        {/* Journey */}
        <div className="space-y-10">
          <ScrollReveal direction="up">
            <div className="space-y-5">
              {about.journey.map((paragraph) => (
                <p
                  key={paragraph.slice(0, 24)}
                  className="text-base leading-[1.75] text-slate-600 sm:text-[17px] dark:text-slate-400"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </ScrollReveal>

          {/* Philosophy */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {about.philosophy.map((principle, index) => {
              const Icon = philosophyIcons[principle.icon] ?? FaFeatherAlt;
              return (
                <ScrollReveal key={principle.title} direction="up" delay={index * 0.06} className="h-full">
                  <SpotlightCard className="h-full p-6">
                    {/* Faint index watermark */}
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute right-4 top-3 font-mono text-4xl font-bold
                                 leading-none text-slate-900/[0.04] dark:text-white/[0.05]"
                    >
                      0{index + 1}
                    </span>

                    <span
                      aria-hidden="true"
                      className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br
                                 from-blue-500/15 to-indigo-500/5 text-blue-500 ring-1 ring-inset ring-blue-500/20
                                 transition-transform duration-500 ease-smooth group-hover:scale-110
                                 group-hover:-rotate-3"
                    >
                      <Icon className="h-4 w-4" />
                    </span>

                    <h3 className="text-[15px] font-semibold text-slate-900 dark:text-white">
                      {principle.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                      {principle.body}
                    </p>

                    {/* Accent bar that draws in on hover */}
                    <span
                      aria-hidden="true"
                      className="mt-5 block h-0.5 w-8 origin-left rounded-full bg-gradient-to-r from-blue-500
                                 to-indigo-400 transition-all duration-500 ease-smooth group-hover:w-14"
                    />
                  </SpotlightCard>
                </ScrollReveal>
              );
            })}
          </div>
        </div>

        {/* Aside */}
        <ScrollReveal direction="left" delay={0.1}>
          <div className="space-y-5 lg:sticky lg:top-28">
            <TerminalFrame title="preferences.config">
              <p className="mb-3 text-[10px] uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-400">
                what_i_enjoy_building
              </p>
              <ul className="space-y-2.5">
                {about.enjoys.map((thing) => (
                  <li key={thing} className="flex items-start gap-2.5">
                    <span aria-hidden="true" className="mt-0.5 text-emerald-500">
                      <FaCheck className="h-2.5 w-2.5" />
                    </span>
                    <span className="text-[12px] leading-relaxed text-slate-700 dark:text-slate-300">
                      {thing}
                    </span>
                  </li>
                ))}
              </ul>
            </TerminalFrame>

            <TerminalFrame title="geo · sync">
              <div className="space-y-3">
                <p className="flex items-center gap-2">
                  <FaMapMarkerAlt className="h-3 w-3 text-emerald-500" aria-hidden="true" />
                  <span>loc: {profile.location}</span>
                </p>
                <p className="flex items-center gap-2">
                  <FaClock className="h-3 w-3 text-cyan-500" aria-hidden="true" />
                  <span>tz: {profile.timezone}</span>
                </p>
                <p className="text-slate-500">hours: flexible across zones</p>
              </div>
              <motion.a
                href="#contact"
                whileHover={{ x: 3 }}
                className="mt-5 inline-flex items-center gap-2 text-[12px] font-semibold text-emerald-600 dark:text-emerald-400"
              >
                ./contact --init
                <span aria-hidden="true">&rarr;</span>
              </motion.a>
            </TerminalFrame>
          </div>
        </ScrollReveal>
      </div>
    </Section>
  );
}
